// Wizardify — guided, step-by-step onboarding wizard that floats over the
// dashboard. Onboards a brand-new customer end-to-end, in the order you'd
// actually do it:
//   Business (tenant) → [Remote SBC, if remote] → Users → Phone line (trunk)
//   → Inbound routing → Desk phones → Done
// It does REAL work via existing APIs. For a REMOTE office the SBC doesn't exist
// yet — so the tenant is created first with a temporary realm, then the SBC step
// creates that customer's SBC, hands over the installer, and the realm sets
// itself from the SBC's auto-detected LAN IP once it connects. Carrier trunk +
// phone provisioning open on their own pages; the wizard waits + detects.

import { useEffect, useState } from 'react';
import {
  Wand2, Building2, Users as UsersIcon, PhoneCall, ArrowDownToLine, Headphones,
  CheckCircle2, ChevronLeft, ChevronRight, X, Plus, Trash2, ExternalLink,
  Loader2, RotateCw, Info, Server, BellRing,
} from 'lucide-react';
import { api } from '../lib/api';

type Props = { open: boolean; onClose: () => void; onDone?: () => void; serverIp?: string; fqdn?: string };

const STEP_DEFS: Record<string, { label: string; icon: any }> = {
  welcome:  { label: 'Welcome',      icon: Wand2 },
  business: { label: 'Business',     icon: Building2 },
  sbc:      { label: 'Remote SBC',   icon: Server },
  users:    { label: 'Users',        icon: UsersIcon },
  ringgroup:{ label: 'Ring group',   icon: BellRing },
  trunk:    { label: 'Phone line',   icon: PhoneCall },
  inbound:  { label: 'Call routing', icon: ArrowDownToLine },
  phones:   { label: 'Desk phones',  icon: Headphones },
  done:     { label: 'Done',         icon: CheckCircle2 },
};

export function OnboardingWizard({ open, onClose, onDone, serverIp, fqdn }: Props) {
  const [stepIdx, setStepIdx] = useState(0);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const [biz, setBiz] = useState<any>({ name: '', locationType: 'remote', realm: '', extFloor: 2000, extCeil: 3000 });
  const [domainUuid, setDomainUuid] = useState('');

  const [users, setUsers] = useState<any[]>([{ first_name: '', last_name: '', email: '' }]);
  const [createdUsers, setCreatedUsers] = useState<any[]>([]);

  const [trunks, setTrunks] = useState<any[]>([]);
  const [trunkLoading, setTrunkLoading] = useState(false);

  const [inbound, setInbound] = useState<any>({ trunk_uuid: '', did_pattern: '', destination: '' });
  const [inboundCreated, setInboundCreated] = useState(false);

  // Remote-office SBCs — each reports its own LAN IP (local_ip) on heartbeat, so
  // the realm is auto-detected once the SBC connects; the admin never types it.
  const [sbcNodes, setSbcNodes] = useState<any[]>([]);
  const [sbcLoading, setSbcLoading] = useState(false);
  const [selectedSbc, setSelectedSbc] = useState('');
  const [realmSet, setRealmSet] = useState(false);
  const [finalRealm, setFinalRealm] = useState('');

  // The SBC step only exists for a remote office (the SBC is created as part of
  // onboarding this customer). Steps are keyed, not index-hardcoded, so the flow
  // is robust to that conditional step.
  const order = biz.locationType === 'remote'
    ? ['welcome', 'business', 'sbc', 'users', 'ringgroup', 'trunk', 'inbound', 'phones', 'done']
    : ['welcome', 'business', 'users', 'ringgroup', 'trunk', 'inbound', 'phones', 'done'];
  const steps = order.map((k) => ({ key: k, ...STEP_DEFS[k] }));
  const step = Math.min(stepIdx, steps.length - 1);
  const key = steps[step].key;
  const next = () => { setErr(''); setStepIdx(Math.min(step + 1, steps.length - 1)); };
  const back = () => { setErr(''); setStepIdx(Math.max(step - 1, 0)); };

  // Full reset on every (re)open so re-running starts clean.
  useEffect(() => {
    if (!open) return;
    setStepIdx(0); setErr(''); setBusy(false);
    setBiz({ name: '', locationType: 'remote', realm: '', extFloor: 2000, extCeil: 3000 });
    setDomainUuid(''); setUsers([{ first_name: '', last_name: '', email: '' }]); setCreatedUsers([]);
    setTrunks([]); setInbound({ trunk_uuid: '', did_pattern: '', destination: '' }); setInboundCreated(false);
    setSbcNodes([]); setSelectedSbc(''); setRealmSet(false); setFinalRealm('');
  }, [open]);

  // Seed the "local" realm only if the reported server IP is actually private —
  // the dashboard IP can be public/NAT, which is NOT a valid SIP realm.
  useEffect(() => {
    if (biz.locationType === 'local' && !biz.realm && isPrivateIp(serverIp)) setBiz((b: any) => ({ ...b, realm: serverIp }));
  }, [serverIp]); // eslint-disable-line react-hooks/exhaustive-deps

  async function refreshTrunks() {
    setTrunkLoading(true);
    try {
      const all: any = await api.trunks.list();
      setTrunks((all || []).filter((t: any) => !domainUuid || !t.domain_uuid || t.domain_uuid === domainUuid));
    } catch { /* ignore */ } finally { setTrunkLoading(false); }
  }
  async function refreshSbc() {
    setSbcLoading(true);
    try { const ns: any = await api.sbc.nodes(); setSbcNodes(ns || []); }
    catch { /* ignore */ } finally { setSbcLoading(false); }
  }
  useEffect(() => { if (open && (key === 'trunk' || key === 'inbound')) refreshTrunks(); /* eslint-disable-next-line */ }, [step, open]);
  useEffect(() => { if (open && key === 'sbc') refreshSbc(); /* eslint-disable-next-line */ }, [step, open]);

  if (!open) return null;

  async function createBusiness() {
    setErr('');
    if (!biz.name.trim()) return setErr('Business name is required.');
    let realm = (biz.realm || '').trim();
    // Remote: the SBC (and its IP) doesn't exist yet — create with a temporary
    // realm; the SBC step finalizes it once the gateway connects.
    if (biz.locationType === 'remote') realm = `pending-sbc-${Math.random().toString(36).slice(2, 8)}`;
    if (!realm) realm = biz.name.toLowerCase().replace(/[^a-z0-9.]+/g, '');
    if (!realm) return setErr('Enter a SIP realm / domain for this business.');
    setBusy(true);
    try {
      const r: any = await api.tenants.create({
        domain_name: realm,
        domain_description: biz.name.trim(),
        domain_enabled: 'true',
        extension_floor: biz.extFloor, extension_ceiling: biz.extCeil,
        system_extension_floor: 8000, system_extension_ceiling: 9000,
        trunk_limit: 6, transcription_mode: 'none',
      });
      setDomainUuid(r.domain_uuid);
      next();
    } catch (e: any) { setErr(e?.message || 'Failed to create business'); }
    finally { setBusy(false); }
  }

  async function setRealmFromSbc() {
    setErr('');
    const node = sbcNodes.find((n) => n.id === selectedSbc);
    if (!node) return setErr('Pick this customer’s SBC first.');
    if (!node.local_ip) return setErr('That SBC hasn’t reported its LAN IP yet — wait for it to come online, then “Check for it”.');
    setBusy(true);
    try {
      await api.tenants.setRealm(domainUuid, node.local_ip);
      try { await api.sbc.assignTenant(node.id, domainUuid); } catch { /* best-effort link */ }
      setFinalRealm(node.local_ip); setRealmSet(true);
    } catch (e: any) { setErr(e?.message || 'Failed to set the phone realm'); }
    finally { setBusy(false); }
  }

  async function createUsers() {
    setErr('');
    const rows = users.filter((u) => `${u.first_name}${u.last_name}${u.email}`.trim());
    if (!rows.length) return next();
    setBusy(true);
    const made: any[] = [];
    try {
      for (const u of rows) {
        const nx: any = await api.users.nextExtension(domainUuid);
        const ext = nx?.next;
        const res: any = await api.users.create({ extension: ext, first_name: u.first_name, last_name: u.last_name, email: u.email, domain_uuid: domainUuid });
        made.push({ name: `${u.first_name} ${u.last_name}`.trim() || ext, extension: res?.extension || ext });
      }
      setCreatedUsers((prev) => [...prev, ...made]);
      next();
    } catch (e: any) { setCreatedUsers((prev) => [...prev, ...made]); setErr(e?.message || 'Failed while creating users'); }
    finally { setBusy(false); }
  }

  async function createInbound() {
    setErr('');
    if (!inbound.trunk_uuid) return setErr('Choose which trunk receives the calls.');
    if (!inbound.destination) return setErr('Choose a destination extension.');
    setBusy(true);
    try {
      await api.inboundRules.create({
        rule_name: `${biz.name || 'Business'} — main line`,
        trunk_uuid: inbound.trunk_uuid,
        did_pattern: inbound.did_pattern || '',
        condition_type: 'always',
        destination_type: 'extension',
        destination: inbound.destination,
        enabled: true, rule_order: 10,
        domain_uuid: domainUuid || undefined,
      });
      setInboundCreated(true);
      next();
    } catch (e: any) { setErr(e?.message || 'Failed to create inbound rule'); }
    finally { setBusy(false); }
  }

  function finish() { try { localStorage.setItem('wizardify-dismissed', '1'); } catch {} onDone?.(); onClose(); }
  function dismiss() { try { localStorage.setItem('wizardify-dismissed', '1'); } catch {} onClose(); }

  const realmHelp = biz.locationType === 'local'
    ? "Phones on the same LAN as the server. Use the server's LAN IP (e.g. 192.168.2.66) — phones register directly to it."
    : 'Phones that register over the internet to a domain name, e.g. acme.stratus.client.computers2go.cloud.';

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" role="dialog" aria-modal>
      <div className="card w-full max-w-3xl max-h-[92vh] flex flex-col p-0 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="font-semibold flex items-center gap-2 text-lg">
            <Wand2 size={20} className="text-primary" /> Wizardify <span className="text-textSecondary text-sm font-normal">· Guided setup</span>
          </h2>
          <button onClick={dismiss} className="btn-ghost p-1" title="Close"><X size={18} /></button>
        </div>

        {/* Stepper */}
        <div className="px-4 pt-3">
          <div className="flex items-center gap-1 overflow-x-auto pb-2">
            {steps.map((s, i) => {
              const Icon = s.icon; const active = i === step; const done = i < step;
              return (
                <div key={s.key} className="flex items-center gap-1 shrink-0">
                  <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs whitespace-nowrap
                    ${active ? 'bg-primary text-white' : done ? 'text-success' : 'text-textSecondary'}`}>
                    {done ? <CheckCircle2 size={14} /> : <Icon size={14} />} {s.label}
                  </div>
                  {i < steps.length - 1 && <ChevronRight size={12} className="text-textSecondary" />}
                </div>
              );
            })}
          </div>
        </div>

        {/* Body */}
        <div className="overflow-auto p-5 flex-1 space-y-4">
          {err && <div className="text-sm text-danger bg-danger/10 border border-danger/30 rounded-md px-3 py-2">{err}</div>}

          {key === 'welcome' && (
            <div className="space-y-3">
              <h3 className="text-xl font-semibold">Let’s onboard a new customer 🪄</h3>
              <p className="text-sm text-textSecondary">This wizard takes you through everything to get a new business making and taking calls — in order, no Stratus expertise needed:</p>
              <ul className="text-sm space-y-2">
                <WelcomeRow icon={Building2} title="Create the business" desc="A tenant with its own extension range." />
                <WelcomeRow icon={Server} title="Set up its SBC (remote offices)" desc="Install at the site — its IP is detected automatically." />
                <WelcomeRow icon={UsersIcon} title="Add users" desc="People get extensions + web logins automatically." />
                <WelcomeRow icon={BellRing} title="Create a ring group" desc="Ring several extensions together (Sales, Front desk…)." />
                <WelcomeRow icon={PhoneCall} title="Connect a phone line" desc="A SIP trunk from your carrier (e.g. Twilio)." />
                <WelcomeRow icon={ArrowDownToLine} title="Route incoming calls" desc="Send the phone number to a person or group." />
                <WelcomeRow icon={Headphones} title="Provision desk phones" desc="Auto-detect and assign physical phones." />
              </ul>
              <p className="text-xs text-textSecondary">You can skip any step and come back later. Nothing is created until you click the action button on each step.</p>
            </div>
          )}

          {key === 'business' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Create the business</h3>
              <L label="Business name *">
                <input className="input" autoFocus value={biz.name} placeholder="Acme Plumbing"
                  onChange={(e) => setBiz({ ...biz, name: e.target.value })} />
              </L>
              <L label="Where are the desk phones?">
                <div className="grid sm:grid-cols-3 gap-2">
                  {[
                    { v: 'local', t: 'Same network', d: 'Local / HQ' },
                    { v: 'remote', t: 'Remote office', d: 'Behind an SBC' },
                    { v: 'cloud', t: 'Over the internet', d: 'Register to a domain' },
                  ].map((o) => (
                    <button key={o.v} type="button" onClick={() => setBiz({ ...biz, locationType: o.v, realm: defaultRealm(o.v, serverIp, fqdn) })}
                      className={`text-left rounded-md border p-2 transition ${biz.locationType === o.v ? 'border-primary ring-2 ring-primary' : 'border-border hover:border-primary'}`}>
                      <div className="text-sm font-medium">{o.t}</div>
                      <div className="text-xs text-textSecondary">{o.d}</div>
                    </button>
                  ))}
                </div>
              </L>
              {biz.locationType === 'remote' ? (
                <div className="text-sm text-textSecondary bg-elevated border border-border rounded-md px-3 py-2 flex gap-2">
                  <Info size={16} className="shrink-0 mt-0.5 text-primary" />
                  <span>No IP to enter here. After you create this customer, the <strong>next step</strong> sets up their SBC and configures the phone realm <strong>automatically</strong> from the SBC’s detected LAN IP.</span>
                </div>
              ) : (
                <>
                  <L label="SIP realm / domain *">
                    <input className="input font-mono" value={biz.realm}
                      placeholder={biz.locationType === 'cloud' ? 'acme.example.com' : '192.168.2.66  (server LAN IP)'}
                      onChange={(e) => setBiz({ ...biz, realm: e.target.value })} />
                  </L>
                  <div className="text-xs text-textSecondary bg-elevated border border-border rounded-md px-3 py-2 flex gap-2">
                    <Info size={14} className="shrink-0 mt-0.5 text-primary" /><span>{realmHelp}</span>
                  </div>
                </>
              )}
              <details className="text-sm">
                <summary className="cursor-pointer text-textSecondary">Advanced: extension range</summary>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <L label="From"><input type="number" className="input" value={biz.extFloor} onChange={(e) => setBiz({ ...biz, extFloor: parseInt(e.target.value) || 0 })} /></L>
                  <L label="To"><input type="number" className="input" value={biz.extCeil} onChange={(e) => setBiz({ ...biz, extCeil: parseInt(e.target.value) || 0 })} /></L>
                </div>
              </details>
            </div>
          )}

          {key === 'sbc' && (
            <div className="space-y-3">
              <h3 className="text-lg font-semibold">Set up {biz.name || 'the business'}’s SBC</h3>
              <p className="text-sm text-textSecondary">A remote office connects through an <strong>SBC</strong> installed at that site. Create it for this customer and download the installer — once it’s installed and connects, its LAN IP becomes this business’s phone realm <strong>automatically</strong>. No IP lookup needed.</p>
              <div className="flex flex-wrap gap-2">
                <button className="btn-primary inline-flex items-center gap-2" onClick={() => window.open('/sbc-onboarding', '_blank')}><ExternalLink size={14} /> Create &amp; download the SBC</button>
                <button className="btn-ghost inline-flex items-center gap-2" onClick={refreshSbc} disabled={sbcLoading}>
                  {sbcLoading ? <Loader2 size={14} className="animate-spin" /> : <RotateCw size={14} />} Check for it
                </button>
              </div>
              <L label="This customer’s SBC (detected automatically once online)">
                <select className="input font-mono" value={selectedSbc} onChange={(e) => setSelectedSbc(e.target.value)}>
                  <option value="">{sbcLoading ? 'Loading…' : '— select the SBC —'}</option>
                  {sbcNodes.map((n) => (
                    <option key={n.id} value={n.id} disabled={!n.local_ip}>
                      {n.name} · {n.local_ip || 'no IP yet'} {n.status === 'online' ? '(online)' : '(offline)'}
                    </option>
                  ))}
                </select>
              </L>
              {realmSet ? (
                <div className="text-sm text-success flex items-center gap-1"><CheckCircle2 size={14} /> Phone realm set to <span className="font-mono">{finalRealm}</span> — this customer’s phones will register here.</div>
              ) : (
                <button className="btn-primary inline-flex items-center gap-2" disabled={busy || !selectedSbc} onClick={setRealmFromSbc}>
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <>Use this SBC — set the phone realm</>}
                </button>
              )}
              <div className="text-xs text-textSecondary bg-elevated border border-border rounded-md px-3 py-2 flex gap-2">
                <Info size={14} className="shrink-0 mt-0.5 text-primary" /><span>No SBC yet? Click “Create &amp; download the SBC”, install it at the office, then “Check for it” — it appears here with its IP detected. You can also skip and finalize the realm later.</span>
              </div>
            </div>
          )}

          {key === 'users' && (
            <div className="space-y-3">
              <h3 className="text-lg font-semibold">Add users</h3>
              <p className="text-sm text-textSecondary">Each person automatically gets the next free extension, a SIP extension and a web login. Leave blank and skip if you’ll add people later.</p>
              <div className="space-y-2">
                {users.map((u, i) => (
                  <div key={i} className="grid grid-cols-[1fr_1fr_1.4fr_auto] gap-2 items-center">
                    <input className="input" placeholder="First name" value={u.first_name} onChange={(e) => setUsers(users.map((x, j) => j === i ? { ...x, first_name: e.target.value } : x))} />
                    <input className="input" placeholder="Last name" value={u.last_name} onChange={(e) => setUsers(users.map((x, j) => j === i ? { ...x, last_name: e.target.value } : x))} />
                    <input className="input" placeholder="Email (optional)" value={u.email} onChange={(e) => setUsers(users.map((x, j) => j === i ? { ...x, email: e.target.value } : x))} />
                    <button className="btn-ghost p-1 text-danger" disabled={users.length === 1} onClick={() => setUsers(users.filter((_, j) => j !== i))}><Trash2 size={16} /></button>
                  </div>
                ))}
              </div>
              <button className="btn-ghost inline-flex items-center gap-1 text-sm" onClick={() => setUsers([...users, { first_name: '', last_name: '', email: '' }])}>
                <Plus size={14} /> Add another
              </button>
              {createdUsers.length > 0 && (
                <div className="text-sm text-success">Created: {createdUsers.map((u) => `${u.name} (${u.extension})`).join(', ')}</div>
              )}
            </div>
          )}

          {key === 'ringgroup' && (
            <div className="space-y-3">
              <h3 className="text-lg font-semibold">Set up a ring group</h3>
              <p className="text-sm text-textSecondary">A <strong>ring group</strong> rings several extensions together (or in sequence) — e.g. a “Sales” or “Front desk” line. Create one for {biz.name || 'this business'} on the Ring Groups page, adding the users you just made as members.</p>
              <button className="btn-primary inline-flex items-center gap-2" onClick={() => window.open('/ring-groups', '_blank')}><ExternalLink size={14} /> Open Ring Groups</button>
              <div className="text-xs text-textSecondary bg-elevated border border-border rounded-md px-3 py-2 flex gap-2">
                <Info size={14} className="shrink-0 mt-0.5 text-primary" /><span>Add a group, pick its members, and note its extension number — you can point incoming calls at it in the “Route incoming calls” step. Skip if you don’t need one.</span>
              </div>
            </div>
          )}

          {key === 'trunk' && (
            <div className="space-y-3">
              <h3 className="text-lg font-semibold">Connect a phone line</h3>
              <p className="text-sm text-textSecondary">A <strong>trunk</strong> links your carrier (e.g. Twilio) so the business can make and receive outside calls. Trunk setup is carrier-specific, so it opens on its own page — finish there, then come back and click <strong>Check for trunk</strong>.</p>
              <div className="flex flex-wrap gap-2">
                <button className="btn-primary inline-flex items-center gap-2" onClick={() => window.open('/voice-chat', '_blank')}><ExternalLink size={14} /> Open Trunk Setup</button>
                <button className="btn-ghost inline-flex items-center gap-2" onClick={refreshTrunks} disabled={trunkLoading}>
                  {trunkLoading ? <Loader2 size={14} className="animate-spin" /> : <RotateCw size={14} />} Check for trunk
                </button>
              </div>
              <div className="card p-3">
                <div className="text-xs uppercase tracking-wide text-textSecondary mb-2">Trunks detected</div>
                {trunks.length === 0 ? <div className="text-sm text-textSecondary">None yet — add one on the Trunk Setup page, then click “Check for trunk”.</div> : (
                  <ul className="text-sm space-y-1">{trunks.map((t) => <li key={t.gateway_uuid} className="flex items-center gap-2"><PhoneCall size={14} className="text-success" /> {t.gateway_name || t.gateway || t.gateway_uuid?.slice(0, 8)}</li>)}</ul>
                )}
              </div>
              <p className="text-xs text-textSecondary">No carrier yet? Skip this — you can add calling anytime.</p>
            </div>
          )}

          {key === 'inbound' && (
            <div className="space-y-3">
              <h3 className="text-lg font-semibold">Route incoming calls</h3>
              {trunks.length === 0 ? (
                <p className="text-sm text-textSecondary">Add a phone line first (previous step) — incoming routing needs a trunk. You can skip this for now.</p>
              ) : (
                <>
                  <p className="text-sm text-textSecondary">When someone calls the business number, where should it ring?</p>
                  <L label="Receive calls on trunk *">
                    <select className="input" value={inbound.trunk_uuid} onChange={(e) => setInbound({ ...inbound, trunk_uuid: e.target.value })}>
                      <option value="">—</option>
                      {trunks.map((t) => <option key={t.gateway_uuid} value={t.gateway_uuid}>{t.gateway_name || t.gateway_uuid?.slice(0, 8)}</option>)}
                    </select>
                  </L>
                  <L label="Phone number / DID (optional — blank = any)">
                    <input className="input font-mono" placeholder="e.g. 16475551234 or blank" value={inbound.did_pattern} onChange={(e) => setInbound({ ...inbound, did_pattern: e.target.value })} />
                  </L>
                  <L label="Ring extension *">
                    <input className="input font-mono" placeholder="e.g. 2000" value={inbound.destination} onChange={(e) => setInbound({ ...inbound, destination: e.target.value })} />
                  </L>
                  {createdUsers.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      <span className="text-xs text-textSecondary mr-1 mt-1">Quick pick:</span>
                      {createdUsers.map((u) => (
                        <button key={u.extension} type="button" className="badge bg-elevated border border-border hover:border-primary" onClick={() => setInbound({ ...inbound, destination: u.extension })}>{u.extension} · {u.name}</button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {key === 'phones' && (
            <div className="space-y-3">
              <h3 className="text-lg font-semibold">Provision desk phones</h3>
              <p className="text-sm text-textSecondary">Stratus auto-discovers physical phones on the network. Open <strong>Phone Provisioning</strong>, find each phone, and assign it to a user’s extension — Stratus pushes the config automatically.</p>
              <button className="btn-primary inline-flex items-center gap-2" onClick={() => window.open('/phones/pnp', '_blank')}><ExternalLink size={14} /> Open Phone Provisioning</button>
              {biz.locationType === 'remote' && (
                <div className="text-xs text-textSecondary bg-elevated border border-border rounded-md px-3 py-2 flex gap-2">
                  <Info size={14} className="shrink-0 mt-0.5 text-primary" /><span>These phones register through this site’s SBC ({realmSet ? finalRealm : 'realm not set yet — go back to the SBC step'}).</span>
                </div>
              )}
            </div>
          )}

          {key === 'done' && (
            <div className="space-y-3 text-center py-4">
              <CheckCircle2 size={48} className="text-success mx-auto" />
              <h3 className="text-xl font-semibold">All set! 🎉</h3>
              <div className="text-sm text-textSecondary inline-block text-left space-y-1">
                <div>✅ Business: <strong>{biz.name || '—'}</strong></div>
                {biz.locationType === 'remote' && <div>{realmSet ? '✅' : '➖'} Remote SBC / realm: {realmSet ? <span className="font-mono">{finalRealm}</span> : 'pending — finish the SBC step'}</div>}
                {biz.locationType !== 'remote' && <div>✅ Phone realm: <span className="font-mono">{biz.realm}</span></div>}
                <div>{createdUsers.length ? '✅' : '➖'} Users: {createdUsers.length ? createdUsers.map((u) => u.extension).join(', ') : 'none yet'}</div>
                <div>{trunks.length ? '✅' : '➖'} Phone line: {trunks.length ? `${trunks.length} trunk(s)` : 'add later'}</div>
                <div>{inboundCreated ? '✅' : '➖'} Inbound routing: {inboundCreated ? 'configured' : 'add later'}</div>
              </div>
              <p className="text-xs text-textSecondary">You can re-run Wizardify anytime from the dashboard.</p>
            </div>
          )}
        </div>

        {/* Footer nav */}
        <div className="p-4 border-t border-border flex items-center justify-between gap-2">
          <button className="btn-ghost inline-flex items-center gap-1" disabled={step === 0 || busy} onClick={back}>
            <ChevronLeft size={16} /> Back
          </button>
          <div className="flex items-center gap-2">
            {['sbc', 'users', 'ringgroup', 'trunk', 'inbound', 'phones'].includes(key) && (
              <button className="btn-ghost" disabled={busy} onClick={next}>Skip</button>
            )}
            {key === 'welcome' && <button className="btn-primary inline-flex items-center gap-1" onClick={next}>Let’s go <ChevronRight size={16} /></button>}
            {key === 'business' && <button className="btn-primary inline-flex items-center gap-1" disabled={busy} onClick={createBusiness}>{busy ? <Loader2 size={16} className="animate-spin" /> : <>Create business <ChevronRight size={16} /></>}</button>}
            {key === 'sbc' && <button className="btn-primary inline-flex items-center gap-1" onClick={next}>Next <ChevronRight size={16} /></button>}
            {key === 'users' && <button className="btn-primary inline-flex items-center gap-1" disabled={busy} onClick={createUsers}>{busy ? <Loader2 size={16} className="animate-spin" /> : <>Create users <ChevronRight size={16} /></>}</button>}
            {key === 'ringgroup' && <button className="btn-primary inline-flex items-center gap-1" onClick={next}>Next <ChevronRight size={16} /></button>}
            {key === 'trunk' && <button className="btn-primary inline-flex items-center gap-1" onClick={next}>Next <ChevronRight size={16} /></button>}
            {key === 'inbound' && (trunks.length === 0
              ? <button className="btn-primary inline-flex items-center gap-1" onClick={next}>Next <ChevronRight size={16} /></button>
              : <button className="btn-primary inline-flex items-center gap-1" disabled={busy} onClick={createInbound}>{busy ? <Loader2 size={16} className="animate-spin" /> : <>Create rule <ChevronRight size={16} /></>}</button>)}
            {key === 'phones' && <button className="btn-primary inline-flex items-center gap-1" onClick={next}>Finish up <ChevronRight size={16} /></button>}
            {key === 'done' && <button className="btn-primary inline-flex items-center gap-1" onClick={finish}>Done <CheckCircle2 size={16} /></button>}
          </div>
        </div>
      </div>
    </div>
  );
}

// A public/NAT address is not a usable SIP realm for LAN phones — only accept
// RFC-1918 ranges when auto-seeding the "local" realm.
function isPrivateIp(ip?: string) { return !!ip && /^(10\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.)/.test(ip); }
function defaultRealm(type: string, serverIp?: string, fqdn?: string) {
  if (type === 'local') return isPrivateIp(serverIp) ? serverIp! : '';
  if (type === 'cloud') return fqdn || '';
  return ''; // remote — finalized automatically from the SBC's detected LAN IP
}

function L({ label, children }: { label: string; children: any }) {
  return <div><label className="block text-sm text-textSecondary mb-1">{label}</label>{children}</div>;
}
function WelcomeRow({ icon: Icon, title, desc }: any) {
  return (
    <li className="flex items-start gap-3">
      <div className="rounded-md bg-primary/15 text-primary p-1.5 mt-0.5"><Icon size={16} /></div>
      <div><div className="font-medium">{title}</div><div className="text-textSecondary text-xs">{desc}</div></div>
    </li>
  );
}
